import * as React from "react";
import "./startReader.css";

export interface ReadingSlide {
  tab: string; // short tab label, e.g. "Why it matters"
  headline: string; // big bold headline
  sections: { h: string; body: string }[]; // sub-head + paragraph, in order
  illustration: React.ComponentType; // the (animated) inline-SVG illustration for this slide
  caption?: string; // small muted note under the illustration
}

export interface StartReaderProps {
  courseTitle: string; // centered in the top bar
  kicker?: string; // small label above headline, e.g. "Secure development · AI coding assistants"
  readMinutes: number; // e.g. 2  -> top-right "2 min read"
  slides: ReadingSlide[]; // 3 slides. Tabs come from slides[].tab
  ready: boolean; // room is renderable -> enable Start
  loadedBytes: number; // for the progress bar
  totalBytes: number; // 0 if unknown (show an indeterminate bar)
  estimateSeconds: number | null; // remaining download seconds, null if unknown
  onStart: () => void; // call only when ready
  onBack?: () => void; // optional: the "← Course overview" link
}

/** More sections than this switches the copy column to the compact two-column layout. */
const DENSE_AFTER = 5;

const mb = (bytes: number): string => (Math.max(0, bytes) / 1e6).toFixed(1);

function estimateLabel(seconds: number): string {
  const s = Math.max(1, Math.round(seconds));
  return s > 60 ? `About ${Math.ceil(s / 60)} min to download` : `About ${s} sec to download`;
}

export function StartReader(props: StartReaderProps): JSX.Element | null {
  const { courseTitle, kicker, readMinutes, slides, ready, loadedBytes, totalBytes, estimateSeconds, onStart, onBack } = props;

  const uid = React.useId();
  const [index, setIndex] = React.useState(0);
  const copyRef = React.useRef<HTMLDivElement | null>(null);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const count = slides.length;
  const cur = count === 0 ? 0 : Math.min(index, count - 1);

  React.useEffect(() => {
    if (copyRef.current) copyRef.current.scrollTop = 0;
  }, [cur]);

  if (count === 0) return null;

  const slide = slides[cur];
  const next = cur < count - 1 ? slides[cur + 1] : null;
  const dense = slide.sections.length > DENSE_AFTER;
  const Art = slide.illustration;

  const tabId = (i: number): string => `${uid}-tab-${i}`;
  const panelId = `${uid}-panel`;

  const goTo = (i: number, focus: boolean): void => {
    setIndex(i);
    if (focus) tabRefs.current[i]?.focus();
  };

  const onTabKey = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    let target = -1;
    if (e.key === "ArrowRight") target = (cur + 1) % count;
    else if (e.key === "ArrowLeft") target = (cur - 1 + count) % count;
    else if (e.key === "Home") target = 0;
    else if (e.key === "End") target = count - 1;
    if (target >= 0) {
      e.preventDefault();
      goTo(target, true);
    }
  };

  /* ---- download progress ---- */
  const known = totalBytes > 0;
  const rawPct = known ? Math.round((Math.max(0, loadedBytes) / totalBytes) * 100) : 0;
  const pct = ready ? 100 : Math.max(0, Math.min(99, rawPct));
  const indeterminate = !ready && !known;

  const meta: string[] = [];
  if (known) {
    meta.push(`${pct}%`, `${mb(Math.min(loadedBytes, totalBytes))} / ${mb(totalBytes)} MB`);
  } else if (loadedBytes > 0) {
    meta.push(`${mb(loadedBytes)} MB downloaded`);
  } else {
    meta.push("Preparing download…");
  }
  if (estimateSeconds != null && !ready) meta.push(estimateLabel(estimateSeconds));

  const handleStart = (): void => {
    if (ready) onStart();
  };

  return (
    <section className={`sr-root${dense ? " sr-is-dense" : ""}`} aria-label={courseTitle}>
      <header className="sr-top">
        <div className="sr-top-l">
          {onBack ? (
            <button type="button" className="sr-back" onClick={onBack}>
              <span aria-hidden="true">←</span> Course overview
            </button>
          ) : null}
        </div>
        <h1 className="sr-title">{courseTitle}</h1>
        <div className="sr-top-r">{readMinutes} min read</div>
      </header>

      <nav className="sr-tabs" role="tablist" aria-label="Course summary">
        {slides.map((s, i) => {
          const active = i === cur;
          return (
            <button
              key={`${i}-${s.tab}`}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              id={tabId(i)}
              type="button"
              role="tab"
              className={`sr-tab${active ? " sr-tab-on" : ""}`}
              aria-selected={active}
              aria-controls={panelId}
              tabIndex={active ? 0 : -1}
              onClick={() => goTo(i, false)}
              onKeyDown={onTabKey}
            >
              <span className="sr-tab-n" aria-hidden="true">
                {i + 1}
              </span>
              <span className="sr-tab-t">{s.tab}</span>
            </button>
          );
        })}
      </nav>

      <div className="sr-body" id={panelId} role="tabpanel" aria-labelledby={tabId(cur)}>
        <div className="sr-copy" ref={copyRef}>
          {kicker ? <p className="sr-kicker">{kicker}</p> : null}
          <h2 className="sr-headline">{slide.headline}</h2>
          <div className={`sr-secs${dense ? " sr-secs-dense" : ""}`}>
            {slide.sections.map((sec, i) => (
              <div className="sr-sec" key={`${cur}-${i}`}>
                <h3 className="sr-sec-h">{sec.h}</h3>
                <p className="sr-sec-p">{sec.body}</p>
              </div>
            ))}
          </div>
        </div>

        <aside className="sr-side">
          <div className="sr-art">
            <Art />
          </div>
          {slide.caption ? <p className="sr-caption">{slide.caption}</p> : null}
          <div className="sr-pager">
            {cur > 0 ? (
              <button type="button" className="sr-btn sr-btn-ghost" onClick={() => goTo(cur - 1, false)}>
                <span aria-hidden="true">←</span> Back
              </button>
            ) : (
              <span />
            )}
            {next ? (
              <button type="button" className="sr-btn sr-btn-ghost" onClick={() => goTo(cur + 1, false)}>
                Next: {next.tab} <span aria-hidden="true">→</span>
              </button>
            ) : null}
          </div>
        </aside>
      </div>

      <footer className="sr-foot">
        <div className="sr-prog">
          <div className="sr-prog-meta">
            {ready ? (
              <span className="sr-done">
                <svg className="sr-done-ic" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                  <polyline points="3.5,8.5 6.5,11.5 12.5,4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Ready — press Start
              </span>
            ) : (
              meta.map((m, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? (
                    <span className="sr-dot" aria-hidden="true">
                      ·
                    </span>
                  ) : null}
                  <span>{m}</span>
                </React.Fragment>
              ))
            )}
          </div>
          <div
            className={`sr-bar${indeterminate ? " sr-bar-ind" : ""}`}
            role="progressbar"
            aria-label="Course download"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={indeterminate ? undefined : pct}
          >
            <div className="sr-bar-fill" style={indeterminate ? undefined : { width: `${pct}%` }} />
          </div>
        </div>

        <div className="sr-start">
          <button type="button" className={`sr-btn sr-go${ready ? " sr-go-on" : ""}`} disabled={!ready} onClick={handleStart}>
            {ready ? (
              <>
                Start <span aria-hidden="true">→</span>
              </>
            ) : (
              <>
                <span className="sr-spin" aria-hidden="true" />
                Loading course…
              </>
            )}
          </button>
          <p className="sr-hint">{ready ? "" : "You can keep reading while the course loads."}</p>
        </div>
      </footer>
    </section>
  );
}

export default StartReader;
